import { useEffect, useRef, useState } from "react";
import { addDays, format, isBefore, parseISO, startOfDay } from "date-fns";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Zap } from "lucide-react";
import { formatCurrency, formatCurrencyWithCents } from "@/lib/currency";
import { usePaymentRuns, usePaymentRunInvoices, type PaymentRun } from "@/hooks/usePaymentRuns";
import { useAutoPaymentRuns } from "@/hooks/useAutoPaymentRuns";
import { PaymentRunDetailModal } from "@/components/par/PaymentRunDetailModal";
import { cn } from "@/lib/utils";
import type { PARInvoiceRow } from "@/lib/par/csvInvoiceParser";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { useSettings } from "@/hooks/useSettings";

const statusColors: Record<PaymentRun["status"], string> = {
  draft: "bg-muted/60 text-muted-foreground border-border",
  processing: "bg-warning/10 text-warning border-warning/20",
  completed: "bg-primary/10 text-primary border-primary/20",
};

function fmtDate(d: string | null | undefined) {
  if (!d) return "—";
  try {
    return format(parseISO(d), "MMM d, yyyy");
  } catch {
    return d;
  }
}

const today = startOfDay(new Date());

function isRunPast(run: PaymentRun): boolean {
  try {
    const sunday = addDays(parseISO(run.pay_date + "T00:00:00"), 6);
    return isBefore(sunday, today);
  } catch {
    return false;
  }
}

interface RunRowProps {
  run: PaymentRun;
  onView: () => void;
}

function SavingsCaptureRing({
  totalSavings,
  eligibleSavings,
}: {
  totalSavings: number;
  eligibleSavings: number;
}) {
  const pct = Math.min(100, Math.round((totalSavings / eligibleSavings) * 100));
  const r = 7;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
      <svg width="18" height="18" viewBox="0 0 18 18" className="shrink-0 -rotate-90">
        <circle
          cx="9"
          cy="9"
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          className="text-muted/40"
        />
        <circle
          cx="9"
          cy="9"
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          className="text-success transition-all duration-500"
        />
      </svg>
      <span className="text-success font-medium">{pct}%</span>
      {" of savings captured · "}
      <span className="text-success font-medium">{formatCurrencyWithCents(totalSavings)}</span>
      {" of "}
      <span className="font-medium">{formatCurrencyWithCents(eligibleSavings)}</span>
    </p>
  );
}

function RunRow({ run, onView }: RunRowProps) {
  const { invoices, fetchInvoices } = usePaymentRunInvoices(run.id);
  const past = isRunPast(run);
  const included = invoices.filter((i) => i.included);
  const totalAmount = included.reduce((s, i) => s + i.outstanding, 0);
  const totalSavings = included.reduce((s, i) => s + i.potential_savings, 0);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  return (
    <Card className="p-4 flex items-start gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <button
            className="text-sm font-semibold truncate text-left hover:underline underline-offset-2 cursor-pointer"
            onClick={onView}
          >
            {run.name}
          </button>
          <Badge className={cn("text-[10px] border shrink-0", statusColors[run.status])}>
            {run.status}
          </Badge>
          {run.auto_generated && (
            <Badge className="text-[10px] border shrink-0 bg-primary/10 text-primary border-primary/20 gap-0.5">
              <Zap className="w-2.5 h-2.5" />
              Auto
            </Badge>
          )}
          {past && (
            <Badge className="text-[10px] border shrink-0 bg-muted/40 text-muted-foreground border-border/60">
              Past
            </Badge>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Pay date {fmtDate(run.pay_date)} · Budget {formatCurrency(run.budget)} · Created{" "}
          {fmtDate(run.created_at)}
        </p>
        {invoices.length > 0 && (
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {included.length} invoice{included.length !== 1 ? "s" : ""} ·{" "}
            {formatCurrency(totalAmount)} ·{" "}
            <span className="text-success font-medium">
              {formatCurrencyWithCents(totalSavings)} savings
            </span>
          </p>
        )}
        {run.status === "completed" && run.eligible_savings && run.eligible_savings > 0 && (
          <SavingsCaptureRing totalSavings={totalSavings} eligibleSavings={run.eligible_savings} />
        )}
      </div>
    </Card>
  );
}

interface PaymentRunsTabProps {
  allInvoices?: PARInvoiceRow[];
  onNewRun?: () => void;
  onViewRun?: (run: PaymentRun) => void;
}

export function PaymentRunsTab({ allInvoices = [], onViewRun }: PaymentRunsTabProps) {
  const { runs, loading, fetchRuns } = usePaymentRuns();
  const { settings } = useSettings();
  const [selectedRun, setSelectedRun] = useState<PaymentRun | null>(null);
  const autoGenAttempted = useRef(false);
  const autoGenInvoiceKey = useRef<string>("");

  const { ensureRunsForWeeks } = useAutoPaymentRuns(fetchRuns);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  // Auto-generate runs for any week with eligible invoices that doesn't have one yet.
  // We intentionally exclude `runs` from the dependency array so that the
  // callback triggered by a successful creation (fetchRuns → setRuns) doesn't
  // loop back and attempt re-creation.  The stable invoiceKey ref prevents
  // redundant calls when allInvoices hasn't changed, and the DB-level unique
  // constraint on (created_by, week_start) is the authoritative dedup guard.
  const runsRef = useRef<typeof runs>(runs);
  useEffect(() => {
    runsRef.current = runs;
  });

  useEffect(() => {
    if (loading) return;
    if (allInvoices.length === 0) return;

    // Compute a stable key from invoice early_pay_due_dates to avoid redundant calls
    const invoiceKey = allInvoices
      .map((i) => i.early_pay_due_date)
      .sort()
      .join(",");
    if (invoiceKey === autoGenInvoiceKey.current) return;
    autoGenInvoiceKey.current = invoiceKey;

    const budget = settings?.early_payment_budget ?? 0;
    // Pass the latest runs via ref to avoid this effect re-triggering when
    // runs changes (e.g. after a successful auto-creation).
    ensureRunsForWeeks(allInvoices, runsRef.current, budget);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allInvoices, loading, settings?.early_payment_budget]);

  // Legacy: also call the edge-function-based generation when the setting is enabled
  // (preserves backward compatibility with the scheduled generation window feature)
  useEffect(() => {
    if (!settings || autoGenAttempted.current) return;
    if (!settings.auto_generate_runs) return;
    autoGenAttempted.current = true;

    const offsets = [-3, -2, -1, 0];
    Promise.all(
      offsets.map((weekOffset) =>
        invokeEdgeFunction<{ created: boolean; run: unknown }>("auto-generate-payment-run", {
          weekOffset,
        }).catch(() => ({ created: false }))
      )
    ).then((results) => {
      if (results.some((r) => r.created)) fetchRuns();
    });
  }, [settings, fetchRuns]);

  const handleView = (run: PaymentRun) => {
    if (onViewRun) {
      onViewRun(run);
    } else {
      setSelectedRun(run);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold">Payment Runs</h3>
        <p className="text-xs text-muted-foreground">
          {runs.length} run{runs.length !== 1 ? "s" : ""}
        </p>
      </div>

      {runs.length === 0 && !loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <CreditCard className="w-10 h-10 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">No payment runs yet</p>
          <p className="text-xs text-muted-foreground">
            Payment runs will appear here once auto-generated each week.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {runs.map((run) => (
            <RunRow key={run.id} run={run} onView={() => handleView(run)} />
          ))}
        </div>
      )}

      <PaymentRunDetailModal
        run={selectedRun}
        open={selectedRun !== null}
        onClose={() => setSelectedRun(null)}
        allInvoices={allInvoices}
      />
    </div>
  );
}
